// Copyright (c) Ping. All rights reserved.
//
// Web host for `addMembersToGroupConversation`.

import {
  type AddGroupMembersResult,
  addMembersToGroupConversation,
  type GroupMemberInput,
  type MessagingClientLike,
  normalizeGroupDeviceRosterMaps,
  sendGroupHydrationMessages,
} from '@ping/ui';
import { buildGroupConversationMetaName } from '@ping/ui/src/conversation/groupConversation';

import { log } from '@core/log/log';
import { claimDevicesForGroupMember } from '@core/network/users';
import { db } from '@core/persistence/db';
import { retrieveIdentity } from '@core/persistence/identity';
import { getCurrentTransport } from '@session/sessionBootstrap';

import { ensureGroupConversation } from './ensureConversation';
import { runSerializedCommit, syncBeforeCommit } from './groupCommitQueue';
import { getOrCreateMlsConversation } from './mlsConversation';
import { persistLocalRoomEvent } from './persistLocalRoomEvent';

export type { AddGroupMembersResult, GroupMemberInput };
export { GroupCreateError } from '@ping/ui';

/** User-facing copy for an MLS epoch desync (server 409 `epoch_advanced`). */
export const EPOCH_DESYNC_MESSAGE =
  "This group's encryption is out of sync — a change advanced on another " +
  'device or a previous one didn’t finish. Recreate the group to add members.';

/**
 * True when an add-members failure is the server's `epoch_advanced` 409 (the
 * local MLS epoch no longer matches the server's — the group is desynced). The
 * structured `serverError`/`status` survive on the JS side, but once the error
 * crosses the SDK's WASM boundary it degrades to a plain `Error` whose message
 * carries "http 409" / "epoch_advanced" — so we match on both.
 */
export function isEpochConflictError(err: unknown): boolean {
  const tagged = err as
    | { serverError?: string; status?: number; statusCode?: number; lastStatus?: number }
    | null;
  if (
    tagged?.serverError === 'epoch_advanced' ||
    tagged?.status === 409 ||
    tagged?.statusCode === 409 ||
    tagged?.lastStatus === 409
  ) {
    return true;
  }
  const msg = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  return /epoch_advanced/i.test(msg) || /\bhttp 409\b/i.test(msg) || /\b409\b/.test(msg);
}

export interface AddWebGroupMembersParams {
  readonly client: MessagingClientLike;
  readonly conversationId: string;
  readonly members: readonly GroupMemberInput[];
  readonly signal?: AbortSignal;
}

/**
 * MLS Commit + Welcome for new peers, merge Dexie roster, optional invite events.
 *
 * Commits are SERIALIZED per conversation and the local group is SYNCED to the
 * server's latest epoch before committing — together with a bounded resync-retry
 * on an epoch conflict, this prevents the `epoch_advanced` (409) desync that
 * otherwise bricks a group. (The send-then-merge SDK guarantees a rejected
 * Commit never advances the local epoch, so the retry can actually succeed.)
 */
export async function addWebGroupMembers(
  params: AddWebGroupMembersParams,
): Promise<AddGroupMembersResult> {
  return runSerializedCommit(params.conversationId, () => addWebGroupMembersInner(params));
}

async function addWebGroupMembersInner(
  params: AddWebGroupMembersParams,
): Promise<AddGroupMembersResult> {
  const identity = await retrieveIdentity();
  if (identity === null) {
    throw new Error('Still signing you in. Try again in a moment.');
  }

  const existing = await db.conversations.get(params.conversationId);
  if (existing?.kind !== 'group') {
    throw new Error('This chat is not a group.');
  }

  // The transport must be live to prime the Welcome's `?recipient=` devices
  // (POST /v1/messages rejects a recipient-less Welcome with 400). Fail fast
  // here rather than after `addMembers` has already advanced the MLS epoch —
  // that would half-add the member (tree updated locally, Welcome never
  // delivered, group left inconsistent).
  const transport = getCurrentTransport();
  if (transport === null) {
    throw new Error('Not connected yet — give it a moment and try again.');
  }

  // Fast-forward to the server's latest epoch before crafting the Commit.
  await syncBeforeCommit(params.client);

  // Bounded resync-retry: if the server still reports an epoch conflict (a
  // genuinely concurrent committer slipped in between sync and send), resync and
  // retry once more at the new epoch.
  const MAX_ATTEMPTS = 2;
  let result: AddGroupMembersResult | undefined;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
    const fresh = await db.conversations.get(params.conversationId);
    const existingPeerIds = fresh?.memberAccountIds ?? existing.memberAccountIds ?? [];

    await getOrCreateMlsConversation({
      client: params.client,
      transport,
      beConversationId: params.conversationId,
      conversationName: existing.name,
      log,
    });

    try {
      result = await addMembersToGroupConversation({
        client: params.client,
        transport,
        conversationIdHex: params.conversationId,
        localAccountId: identity.accountId,
        localDisplayName: identity.displayName,
        existingPeerAccountIds: existingPeerIds,
        newMembers: params.members,
        claimDevicesForAccount: claimDevicesForGroupMember,
        log,
        ...(params.signal !== undefined ? { signal: params.signal } : {}),
      });
      break;
    } catch (err) {
      if (!isEpochConflictError(err) || attempt === MAX_ATTEMPTS - 1) throw err;
      log.warn({
        event: 'webGroup.addMembers.epochConflict.resync',
        attempt: attempt + 1,
        conversationIdPrefix: params.conversationId.slice(0, 8),
      });
      await syncBeforeCommit(params.client);
    }
  }
  if (result === undefined) {
    throw new Error("Couldn't add those members. Try again.");
  }

  const memberDisplayNames: Record<string, string> = {
    ...(existing.memberDisplayNames ?? {}),
  };
  for (const m of params.members) {
    if (m.accountId !== identity.accountId) {
      memberDisplayNames[m.accountId] = m.displayName;
    }
  }

  const rosterPatch = normalizeGroupDeviceRosterMaps({
    memberDeviceNames: {
      ...(existing.memberDeviceNames ?? {}),
      ...result.memberDeviceNames,
    },
    memberDeviceAccounts: {
      ...(existing.memberDeviceAccounts ?? {}),
      ...result.memberDeviceAccounts,
    },
  });

  await ensureGroupConversation({
    conversationId: params.conversationId,
    displayName: existing.name ?? '',
    memberAccountIds: result.memberAccountIds,
    memberDisplayNames,
    ...(rosterPatch.memberDeviceNames !== undefined
      ? { memberDeviceNames: rosterPatch.memberDeviceNames }
      : {}),
    ...(rosterPatch.memberDeviceAccounts !== undefined
      ? { memberDeviceAccounts: rosterPatch.memberDeviceAccounts }
      : {}),
  });

  const hydrationDisplayNames: Record<string, string> = {
    ...memberDisplayNames,
    [identity.accountId]: identity.displayName,
  };

  await sendGroupHydrationMessages({
    conv: result.conv,
    inviterAccountId: identity.accountId,
    inviterDisplayName: identity.displayName,
    title: existing.name?.trim(),
    memberAccountIds: result.memberAccountIds,
    memberDisplayNames: hydrationDisplayNames,
    // Ride the group avatar id on the hydration broadcast so a fresh web joiner
    // — who can't read the GroupContext meta on their Welcome — still gets the
    // group picture (the meta-rewrite below is best-effort / late).
    ...(existing.avatarMediaId !== undefined && existing.avatarMediaId.length > 0
      ? { avatarMediaId: existing.avatarMediaId }
      : {}),
    log,
  });

  // AUTHORITATIVE META REWRITE (late-joiner convergence). Online members already
  // converge via the `com.ping.group_name` broadcast above, but a member who
  // joins LATER parses the MLS GroupContext meta on their Welcome — which,
  // without this, still carries only the CREATION roster. Rewrite the meta with
  // the FULL new roster so every future joiner (and every meta-reconcile in
  // `useIncomingConversations`) sees the complete member list. Best-effort: this
  // is an epoch-advancing self-update commit and the add ALREADY succeeded, so a
  // failure here (e.g. a concurrent committer → 409) must never fail the add —
  // B4's server-roster reconcile still heals late joiners as the backstop.
  if (typeof result.conv.setConversationName === 'function') {
    try {
      const metaName = buildGroupConversationMetaName({
        inviter: { accountId: identity.accountId, displayName: identity.displayName },
        members: result.memberAccountIds.map((id) => ({
          accountId: id,
          displayName: memberDisplayNames[id] ?? '',
        })),
        ...(existing.name !== undefined && existing.name.trim().length > 0
          ? { title: existing.name.trim() }
          : {}),
        ...(existing.avatarMediaId !== undefined && existing.avatarMediaId.length > 0
          ? { avatarMediaId: existing.avatarMediaId }
          : {}),
      });
      await result.conv.setConversationName(metaName);
      log.info({
        event: 'webGroup.addMembers.metaRewritten',
        conversationIdPrefix: params.conversationId.slice(0, 8),
        rosterSize: result.memberAccountIds.length,
      });
    } catch (err) {
      log.warn({
        event: 'webGroup.addMembers.metaRewrite.failed',
        conversationIdPrefix: params.conversationId.slice(0, 8),
        error: (err as Error).message,
      });
    }
  }

  await sendAddMemberInviteEvents({
    conv: result.conv,
    conversationId: params.conversationId,
    inviterAccountId: identity.accountId,
    inviterDisplayName: identity.displayName,
    addedMembers: params.members.filter((m) =>
      result.addedPeerAccountIds.includes(m.accountId),
    ),
    // Ride the authoritative post-add roster on the invite event so the SAME
    // reliably-delivered "X invited Y" event converges every recipient's member
    // list directly — no dependency on the separate group_name broadcast.
    memberAccountIds: result.memberAccountIds,
    memberDisplayNames: hydrationDisplayNames,
  });

  log.info({
    event: 'webGroup.addMembers.ok',
    conversationIdPrefix: params.conversationId.slice(0, 8),
    addedCount: result.addedPeerAccountIds.length,
  });

  return result;
}

async function sendAddMemberInviteEvents(params: {
  readonly conv: AddGroupMembersResult['conv'];
  readonly conversationId: string;
  readonly inviterAccountId: string;
  readonly inviterDisplayName: string;
  readonly addedMembers: readonly GroupMemberInput[];
  readonly memberAccountIds: readonly string[];
  readonly memberDisplayNames: Readonly<Record<string, string>>;
}): Promise<void> {
  for (const member of params.addedMembers) {
    try {
      const invite = {
        kind: 'com.ping.invite',
        payload: {
          actorDisplayName: params.inviterDisplayName,
          inviteeDisplayName: member.displayName,
          // Authoritative roster on the event itself → reliable roster
          // convergence for every recipient (see InviteBody).
          inviteeAccountId: member.accountId,
          memberAccountIds: [...params.memberAccountIds],
          memberDisplayNames: params.memberDisplayNames,
        },
      };
      const sent = await params.conv.send(new TextEncoder().encode(JSON.stringify(invite)));
      // Local echo so the inviter (actor) sees the "added X" event bubble too.
      await persistLocalRoomEvent({
        conversationId: params.conversationId,
        senderAccountId: params.inviterAccountId,
        envelope: invite,
        contentHash: sent.content_hash,
      });
    } catch (err) {
      log.warn({
        event: 'webGroup.inviteEvent.failed',
        error: (err as Error).message,
      });
    }
  }
}
